/**
 * DifficultyToggle — two-card toggle for `padrao` vs `hard`.
 *
 * Hard is visually emphasised with a red gradient + "Difícil" badge so users
 * understand the leaderboard tradeoff.
 *
 * Spec: docs/architecture/simulador-spec.md §3.4.
 */
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { SimulatorDifficulty } from "../types";

export interface DifficultyToggleProps {
  value: SimulatorDifficulty;
  onChange: (next: SimulatorDifficulty) => void;
  disabled?: boolean;
}

interface Option {
  id: SimulatorDifficulty;
  title: string;
  subtitle: string;
  hard?: boolean;
}

const OPTIONS: Option[] = [
  {
    id: "padrao",
    title: "Padrão",
    subtitle: "Paciente equilibrado, ideal para treinar fundamentos.",
  },
  {
    id: "hard",
    title: "Difícil",
    subtitle: "Paciente exigente, objeções profundas e comparações fortes.",
    hard: true,
  },
];

export function DifficultyToggle({ value, onChange, disabled }: DifficultyToggleProps) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      role="radiogroup"
      aria-label="Selecione a dificuldade"
    >
      {OPTIONS.map((opt) => {
        const isSelected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={cn(
              "relative text-left rounded-xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected
                ? "border-2 border-primary bg-primary/5 shadow-md"
                : "border-border/50 bg-card hover:border-border",
              opt.hard &&
                !isSelected &&
                "bg-gradient-to-br from-destructive/5 via-card to-card",
              opt.hard &&
                isSelected &&
                "border-destructive bg-destructive/5",
              disabled && "opacity-50 cursor-not-allowed",
            )}
            data-testid={`difficulty-${opt.id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-foreground">{opt.title}</span>
              {opt.hard ? (
                <Badge variant="destructive" className="text-[10px]">
                  Difícil
                </Badge>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{opt.subtitle}</p>
          </button>
        );
      })}
    </div>
  );
}
